import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UrlService {

  /**
   * Calculates the base URL for the application, respecting the
   * environment's domainUrl and baseHref. Ensures correct handling of slashes.
   * This URL is suitable for redirects or constructing full application URLs.
   * It will include the trailing slash.
   * @returns The application's base URL, ending with a slash.
   */
  getApplicationBaseUrl(): string {
    const domain = environment.domainUrl.replace(/\/$/, ''); // Remove trailing slash from domain
    let base = environment.baseHref; // e.g., /widget2025/ or /

    // Ensure base starts with /
    if (!base.startsWith('/')) {
      base = '/' + base;
    }
    // Ensure base ends with / if it's not just the root '/'
    if (!base.endsWith('/') && base !== '/') {
      base = base + '/';
    }
    // Handle case if environment.baseHref was empty string resulting in '//'
    if (base === '//') {
      base = '/';
    }

    // Combine domain + base (which now correctly ends with /)
    return domain + base;
  }

  /**
   * Constructs a full URL for a specific internal route, including the hash
   * if HashLocationStrategy is used (which it is in this project).
   *
   * @param routePath The internal route path (e.g., ROUTE_PATHS.FORM or ROUTE_PATHS.CHECK_STATUS)
   * @param queryParams Optional query parameters as an object or URLSearchParams
   * @returns The full URL string including domain, baseHref, hash, route, and query params.
   */
  getUrlForRoute(routePath: string, queryParams?: URLSearchParams | Record<string, string>): string {
    const baseUrl = this.getApplicationBaseUrl();
    let url = baseUrl + '#/' + routePath.replace(/^\//, ''); // Add hash and route path
    if (queryParams) {
      const params = queryParams instanceof URLSearchParams
        ? queryParams.toString()
        : new URLSearchParams(queryParams).toString();
      if (params.length > 0) url += '?' + params.toString();
    }
    return url;
  }

}
